import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import { AvailabilityDto } from "@insite/client-framework/Types/ApiModels";
import Phone from "@insite/mobius/Icons/Phone";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomState } from "@tggstore/Store/Reducers";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
}

const mapStateToProps = (state: CustomState) => {
    const { isAuthenticated, rememberMe, isGuest } = getSession(state);
    const userIsAuthenticated = (isAuthenticated || rememberMe) && !isGuest;
    const defaultWarehouse = state.customDefaultWarehouse.warehouse?.id || "";
    const wareHouseList = state.customWareHouseList;
    const cart = getCurrentCartState(state).value;
    const productProperties = state.customProductCategories.productProperties;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        userIsAuthenticated,
        defaultWarehouse,
        wareHouseList,
        isProductDetailPage: getCurrentPage(state).type === "ProductDetailsPage",
        isCartPage: getCurrentPage(state).type === "CartPage",
        isProductListPage: getCurrentPage(state).type === "ProductListPage",
        cart,
        productProperties,
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

export interface CustomProductWarehouseStyles {
    wrapper?: InjectableCss;
    selectedWarehouseLabelText?: TypographyPresentationProps;
    selectedWarehouseValueText?: TypographyPresentationProps;
    selectedWarehouseWrapper?: InjectableCss;
    inStockLabelText?: TypographyPresentationProps;
    inStockValueText?: TypographyPresentationProps;
    inStockLabelWrapper?: InjectableCss;
    totalStockLabelWrapper?: InjectableCss;
    totalStockValueText?: TypographyPresentationProps;
    boxQtyWrapper?: InjectableCss;

    // Verndale Customization ticket: TGGOPT-1810

    zeroAvailabilityWrapper?: InjectableCss;
    phoneLink?: LinkPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    // End Verndale Customization ticket: TGGOPT-1810
}

export const customProductWarehouseStyles: CustomProductWarehouseStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    selectedWarehouseWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            margin-top: 10px;
            flex-direction: column;
        `,
    },
    selectedWarehouseLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    selectedWarehouseValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    inStockLabelWrapper: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    inStockLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    inStockValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            padding-left: 5px;
            ${wrapInContainerStyles}
        `,
    },
    totalStockValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            padding-left: 5px;
            ${wrapInContainerStyles}

            @media (max-width: 768px) {
                padding-left: 0;
            }
        `,
    },
    boxQtyWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            margin-top: 0;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },

    // Verndale Customization ticket: TGGOPT-1810
    zeroAvailabilityWrapper: {
        css: css`
            margin-top: 10px;
            margin-bottom: 10px;
        `,
    },
    phoneLink: {
        css: css`
            display: flex;
            padding: 4px 16px 4px 8px;
            align-items: center;
            gap: 4px;
            background-color: #ebeef7;
            cursor: unset;
            &:focus {
                outline: none;
            }

            @media (max-width: 768px) {
                padding: 4px 8px 4px 4px;
            }
        `,
        icon: {
            iconProps: {
                src: Phone,
            },
            position: "left",
        },
        typographyProps: {
            css: css`
                padding-left: 10px;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    // End Verndale Customization ticket: TGGOPT-1810
};

const styles = customProductWarehouseStyles;

const CustomProductWarehouse = ({
    productContext,
    userIsAuthenticated,
    defaultWarehouse,
    wareHouseList,
    isProductDetailPage,
    isCartPage,
    productProperties,
    productId,
    availability,
    isProductListPage,
}: Props) => {
    const { inventory, unitOfMeasure } = productContext?.productInfo || {};
    const { properties } = productContext?.product || {};
    const [warehouse, setWarehouse] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [warehouseStock, setWarehouseStock] = useState<number | undefined>(undefined);
    const [boxQty, setboxQty] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    if (!userIsAuthenticated) {
        return null;
    }

    useEffect(() => {
        if (wareHouseList && defaultWarehouse) {
            const { warehouses } = wareHouseList;
            const warehouse = warehouses.find(warehouse => warehouse.id === defaultWarehouse);
            if (warehouse) {
                setWarehouse(warehouse.description);
                setPhoneNumber(warehouse.phone);
            }
        }
    }, [wareHouseList, defaultWarehouse]);

    useEffect(() => {
        // Reset the warehouse stock when warehouse changes
        setWarehouseStock(undefined);
        setIsLoading(true);

        if (inventory && unitOfMeasure && !isCartPage && warehouse) {
            const availability =
                inventory?.inventoryAvailabilityDtos?.find(o => o.availability?.message.includes(warehouse))
                    ?.availability || undefined;
            if (availability && availability.message && availability.message !== "") {
                const match = availability.message.match(/: (\d+)\s*$/);
                if (match) {
                    const availabilityValue = parseInt(match[1], 10);
                    if (!Number.isNaN(availabilityValue)) {
                        setWarehouseStock(availabilityValue);
                        setIsLoading(false);
                    }
                } else {
                    setWarehouseStock(0);
                    setIsLoading(false);
                }
            } else {
                // No availability message found for this warehouse
                setWarehouseStock(0);
                setIsLoading(false);
            }
        } else if (
            isCartPage &&
            productProperties.length > 0 &&
            availability &&
            availability.message &&
            availability.message !== ""
        ) {
            // Processing for cart page remains the same
            const availabilitySplit = availability.message.split(":");
            if (availabilitySplit.length > 1) {
                const availabilityValue = parseInt(availabilitySplit[availabilitySplit.length - 1], 10);
                if (!Number.isNaN(availabilityValue)) {
                    setWarehouseStock(availabilityValue);
                    setIsLoading(false);
                }
            } else {
                setWarehouseStock(0);
                setIsLoading(false);
            }
        } else {
            // If we don't have the necessary data, set to 0
            setWarehouseStock(0);
            setIsLoading(false);
        }
    }, [inventory, unitOfMeasure, productProperties, productId, warehouse, defaultWarehouse]);

    useEffect(() => {
        if (properties) {
            const property = properties["boxQty"];
            if (property) {
                setboxQty(property);
            }
        }
    }, [properties]);

    useEffect(() => {
        if (warehouseStock === undefined && !isProductListPage) {
            setIsLoading(true);
        } else if ((warehouseStock === undefined || inventory === undefined) && isProductListPage) {
            setIsLoading(true);
        }else if (warehouseStock === 0) {
            setIsLoading(false);
        } else {
            setIsLoading(false);
        }
    }, [warehouseStock, inventory]);

    return (
        <StyledWrapper {...styles.wrapper}>
            {userIsAuthenticated && isLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {userIsAuthenticated && !isLoading && (
                <>
                    <StyledWrapper {...styles.selectedWarehouseWrapper}>
                        <Typography {...styles.selectedWarehouseLabelText}>
                            {translate("Selected Warehouse")}:
                        </Typography>
                        <Typography {...styles.selectedWarehouseValueText} data-test-selector="selectedWarehouse">
                            {warehouse}
                        </Typography>
                    </StyledWrapper>
                    {warehouseStock !== undefined && warehouseStock > 0 && (
                        <StyledWrapper {...styles.inStockLabelWrapper}>
                            <Typography {...styles.inStockLabelText}>{translate("In Stock")}:</Typography>
                            <Typography {...styles.inStockValueText} data-test-selector="inStock">
                                {warehouseStock}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {warehouseStock !== undefined && warehouseStock === 0 && (
                        <StyledWrapper {...styles.zeroAvailabilityWrapper}>
                            <Link {...styles.phoneLink}>{siteMessage("Call_For_Inventory", phoneNumber)}</Link>
                        </StyledWrapper>
                    )}
                    {/* Verndale Customization ticket: TGGOPT-1941 */}
                    {boxQty && isProductDetailPage && (
                        <StyledWrapper {...styles.boxQtyWrapper}>
                            <Typography {...styles.selectedWarehouseLabelText}>
                                {translate("Qty per case/box")}:
                            </Typography>
                            <Typography {...styles.selectedWarehouseValueText} data-test-selector="boxQty">
                                {`# ${boxQty}`}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {/* End Verndale Customization ticket: TGGOPT-1941 */}
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(CustomProductWarehouse));

// Verndale Custom Component : Ticket TGGMS-386
// Product Detail Page and Product List Page, to show
// the selected warehouse and stock availability for the selected warehouse.
